import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { Link } from "react-router";

export default function DoctorAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("patient.name");
  const [startIndex, setStartIndex] = useState(0);

  // ---- NEW STATE ----
  const [cancelId, setCancelId] = useState<number | null>(null);
  const [cancelFullDate, setCancelFullDate] = useState<string | null>(null);
  const [reason, setReason] = useState(localStorage.getItem("doctor_cancel_reason") || "");

  // Generate sliding 7-day window
  const daysWindow = [...Array(7)].map((_, i) => {
    const d = dayjs().add(startIndex + i, "day");
    return {
      label: d.format("ddd DD MMM"),
      value: d.format("YYYY-MM-DD"),
    };
  });

  useEffect(() => {
    axiosInstance.get("/appointment/doctor").then((res) => {
      setAppointments(res.data);
    });
  }, []);

  // Filtering
  const filtered = appointments
    .filter((a: any) => a.date === selectedDate)
    .filter((a: any) =>
      a.patient?.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.patient?.phone?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a: any, b: any) => {
      const keys = sortKey.split(".");
      const aVal = keys.reduce((o, k) => o[k], a);
      const bVal = keys.reduce((o, k) => o[k], b);
      return aVal.toString().localeCompare(bVal.toString());
    });

  // ---- CANCEL SINGLE ----
  const requestCancelSingle = (id: number) => {
    setCancelId(id);
    setReason(localStorage.getItem("doctor_cancel_reason") || "");
  };

  // ---- CANCEL FULL DAY ----
  const requestCancelFullDay = () => {
    setCancelFullDate(selectedDate);
    setReason(localStorage.getItem("doctor_cancel_reason") || "");
  };

  // ---- CONFIRM SINGLE ----
  const confirmCancelSingle = async () => {
    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }

    try {
      await axiosInstance.post("/appointment/cancel", {
        appointmentId: cancelId,
        reasonForCancellation: reason,
      });

      localStorage.setItem("doctor_cancel_reason", reason);

      toast.success("Appointment cancelled");

      const refreshed = await axiosInstance.get("/appointment/doctor");
      setAppointments(refreshed.data);

      setCancelId(null);
    } catch (err) {
      toast.error("Failed to cancel appointment");
    }
  };

  // ---- CONFIRM FULL DAY ----
  const confirmCancelAll = async () => {
    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }

    try {
      await axiosInstance.post("/appointment/cancel-day", {
        date: cancelFullDate,
        reasonForCancellation: reason,
      });

      localStorage.setItem("doctor_cancel_reason", reason);

      toast.success(`All appointments cancelled for ${cancelFullDate}`);

      const refreshed = await axiosInstance.get("/appointment/doctor");
      setAppointments(refreshed.data);

      setCancelFullDate(null);
    } catch (err) {
      toast.error("Failed to cancel day's appointments");
    }
  };

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold mb-4 text-indigo-700">
          Patient Appointments
        </h1>

        {/* CANCEL ALL BUTTON */}
        <button
          onClick={requestCancelFullDay}
          className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800"
        >
          Cancel All for This Day
        </button>
      </div>

      {/* SEARCH */}
      <div className="flex justify-between items-center mb-4">
        <input
          type="text"
          placeholder="Search patient..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full mb-4 px-3 py-2 border rounded-lg"
        />

        {/* SORT */}
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="mb-4 px-3 py-2 border rounded-lg"
        >
          <option value="patient.name">Sort by Name</option>
          <option value="patient.phone">Sort by Phone</option>
        </select>
      </div>

      {/* DAY SLIDER */}
      <div className="flex items-center gap-2 mb-5">
        <button
          disabled={startIndex === 0}
          onClick={() => setStartIndex((prev) => Math.max(prev - 7, 0))}
          className="px-3 py-2 bg-gray-200 rounded disabled:opacity-40"
        >
          ◀
        </button>

        <div className="flex gap-4 overflow-x-auto">
          {daysWindow.map((d) => (
            <button
              key={d.value}
              onClick={() => setSelectedDate(d.value)}
              className={`px-4 py-2 rounded-lg ${selectedDate === d.value
                ? "bg-indigo-600 text-white"
                : "bg-white border"
                }`}
            >
              {d.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setStartIndex((prev) => prev + 7)}
          className="px-3 py-2 bg-gray-200 rounded"
        >
          ▶
        </button>
      </div>



      {/* TABLE */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-indigo-600 text-white">
            <tr>
              <th className="p-3 text-left">Profile</th>
              <th className="p-3 text-left">Patient</th>
              <th className="p-3 text-left">Phone</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Time</th>
              <th className="p-3 text-left">Reports</th>
              <th className="p-3 text-left">Action</th>
            </tr>
          </thead>

          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-4 text-center text-gray-500">
                  No appointments on this day
                </td>
              </tr>
            ) : (
              filtered.map((appt: any) => (
                <tr key={appt.appointmentId} className="border-b">
                  <td className="p-3">
                    <img
                      src={appt.patient?.profile}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  </td>

                  <td className="p-3 font-medium">{appt.patient?.name}</td>
                  <td className="p-3">{appt.patient?.phone}</td>
                  <td className="p-3">{appt.patient?.email}</td>

                  <td className="p-3">
                    {appt.start} – {appt.end}
                  </td>
                  <td className="p-3">
                    <Link className="px-4 py-2 bg-blue-600 text-white rounded-lg" to={`/patients/${appt.patient?.phone}/reports`}>
                      View Reports
                    </Link>
                  </td>

                  <td className="p-3">
                    {appt.status === "CANCELLED" ? (
                      <span className="text-red-500 font-semibold">Cancelled</span>
                    ) : appt.status === "VIEWED" ? (
                      <span className="text-green-600 font-semibold">Viewed</span>
                    ) : appt.status === "BOOKED" && dayjs(appt.date).isBefore(dayjs(), "day") ? (
                      <span className="text-red-600 font-semibold">Missed</span>
                    ) : (
                      <button
                        onClick={() => requestCancelSingle(appt.appointmentId)}
                        className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700"
                      >
                        Cancel
                      </button>
                    )}
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ---- CANCEL MODAL ---- */}
      {(cancelId || cancelFullDate) && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl w-96">
            <h2 className="text-xl font-bold mb-3 text-red-600">
              {cancelId ? "Cancel Appointment" : "Cancel All Appointments"}
            </h2>

            <label className="font-medium">Reason for cancellation</label>
            <textarea
              rows={4}
              className="border rounded w-full p-2 mt-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter cancellation reason..."
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setCancelId(null);
                  setCancelFullDate(null);
                }}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Close
              </button>

              <button
                onClick={() =>
                  cancelId ? confirmCancelSingle() : confirmCancelAll()
                }
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
